/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2019, 2022
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import { AnyAction } from '@reduxjs/toolkit';

import { ConflictsSlice } from '../../../../ts/state';
import * as actionType from './actionTypes';

const initialState: ConflictsSlice = {
  allConflictsLoading: false,
  allConflicts: null,
  allConflictsError: null,
  intentConflictsLoading: false,
  intentConflicts: null,
  intentConflictsError: null,
  exampleConflictsLoading: false,
  exampleConflicts: null,
  exampleConflictsError: null
};

const getAllIntentConflictsStart = (state: ConflictsSlice): ConflictsSlice => ({
  ...state,
  allConflictsLoading: true,
  allConflicts: null,
  allConflictsError: null
});

const getAllIntentConflictsSuccess = (
  state: ConflictsSlice,
  action: AnyAction
): ConflictsSlice => ({
  ...state,
  allConflictsLoading: false,
  allConflicts: action.allIntentConflicts
});

const getAllIntentConflictsError = (
  state: ConflictsSlice,
  action: AnyAction
): ConflictsSlice => ({
  ...state,
  allConflictsLoading: false,
  allConflicts: null,
  allConflictsError: action.error
});

const getConflictsForIntentStart = (state: ConflictsSlice): ConflictsSlice => ({
  ...state,
  intentConflictsLoading: true,
  intentConflicts: null,
  intentConflictsError: null
});

const getConflictsForIntentSuccess = (
  state: ConflictsSlice,
  action: AnyAction
): ConflictsSlice => ({
  ...state,
  intentConflictsLoading: false,
  intentConflicts: action.conflictsForIntent
});

const getConflictsForIntentError = (
  state: ConflictsSlice,
  action: AnyAction
): ConflictsSlice => ({
  ...state,
  intentConflictsLoading: false,
  intentConflicts: null,
  intentConflictsError: action.error
});

const getConflictsForIntentExampleStart = (
  state: ConflictsSlice
): ConflictsSlice => ({
  ...state,
  exampleConflictsLoading: true,
  exampleConflicts: null,
  exampleConflictsError: null
});

const getConflictsForIntentExampleSuccess = (
  state: ConflictsSlice,
  action: AnyAction
): ConflictsSlice => ({
  ...state,
  exampleConflictsLoading: false,
  exampleConflicts: action.conflictsForIntentExample
});

const getConflictsForIntentExampleError = (
  state: ConflictsSlice,
  action: AnyAction
): ConflictsSlice => ({
  ...state,
  exampleConflictsLoading: false,
  exampleConflicts: null,
  exampleConflictsError: action.error
});

export const reducer = (
  state: ConflictsSlice = initialState,
  action: AnyAction = {} as AnyAction
): ConflictsSlice => {
  switch (action.type) {
    case actionType.FETCH_ALL_INTENT_CONFLICTS:
      return getAllIntentConflictsStart(state);
    case actionType.FETCH_ALL_INTENT_CONFLICTS_SUCCESS:
      return getAllIntentConflictsSuccess(state, action);
    case actionType.FETCH_ALL_INTENT_CONFLICTS_ERROR:
      return getAllIntentConflictsError(state, action);

    case actionType.FETCH_SINGLE_INTENT_CONFLICTS:
      return getConflictsForIntentStart(state);
    case actionType.FETCH_SINGLE_INTENT_CONFLICTS_SUCCESS:
      return getConflictsForIntentSuccess(state, action);
    case actionType.FETCH_SINGLE_INTENT_CONFLICTS_ERROR:
      return getConflictsForIntentError(state, action);

    case actionType.FETCH_INTENT_EXAMPLE_CONFLICTS:
      return getConflictsForIntentExampleStart(state);
    case actionType.FETCH_INTENT_EXAMPLE_CONFLICTS_SUCCESS:
      return getConflictsForIntentExampleSuccess(state, action);
    case actionType.FETCH_INTENT_EXAMPLE_CONFLICTS_ERROR:
      return getConflictsForIntentExampleError(state, action);

    default:
      return state;
  }
};

export default reducer;
